import clsx from "clsx";
import { Mic, Square } from "lucide-react";
import type { ReactNode } from "react";

import type { DictationStatus } from "@/hooks/useDictation";

interface DictationButtonProps {
  variant: "cyan" | "magenta";
  status: DictationStatus;
  isSupported: boolean;
  disabled?: boolean;
  onClick: () => void;
  onPointerDown?: React.PointerEventHandler<HTMLButtonElement>;
  className?: string;
  icon?: ReactNode;
}

function getButtonLabel(status: DictationStatus, isSupported: boolean): string {
  if (!isSupported) {
    return "Dictation is not supported in this browser";
  }

  if (status === "recording") {
    return "Stop recording";
  }

  if (status === "transcribing") {
    return "Transcribing...";
  }

  if (status === "processing") {
    return "Processing...";
  }

  return "Start recording";
}

export function DictationButton({
  variant,
  status,
  isSupported,
  disabled,
  onClick,
  onPointerDown,
  className,
  icon,
}: DictationButtonProps): React.JSX.Element {
  const label = getButtonLabel(status, isSupported);
  const isDisabled =
    disabled || !isSupported || status === "transcribing" || status === "processing";

  const hoverColor =
    variant === "cyan"
      ? "hover:border-cyber-cyan/50 hover:text-cyber-cyan"
      : "hover:border-cyber-magenta/50 hover:text-cyber-magenta";

  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={onPointerDown}
      disabled={isDisabled}
      className={clsx(
        "inline-flex items-center justify-center p-1 transition-all",
        isDisabled ? "text-cyber-muted/60 cursor-not-allowed" : "text-cyber-muted",
        !isDisabled && status === "idle" && hoverColor,
        status === "recording" && "animate-neon-pulse text-cyber-red hover:text-cyber-red",
        (status === "transcribing" || status === "processing") && "text-cyber-muted opacity-90",
        className,
      )}
      aria-label={label}
      title={label}
    >
      {status === "recording" ? (
        <Square size={12} />
      ) : status === "transcribing" || status === "processing" ? (
        <span className="animate-spin-glow inline-block">◐</span>
      ) : (
        (icon ?? <Mic size={16} />)
      )}
    </button>
  );
}
